/**
 * variant-picker.js — VariantPicker custom element
 *
 * Swatch-based variant selector with:
 * - Color swatches (circle with bg color, reads from value name like "Rouge", "Bleu")
 * - Text pills for size/style variants
 * - Full keyboard navigation (arrow keys, home/end, space/enter)
 * - Automatic sold-out state
 * - Emits variant-change events that ProductForm listens for
 *
 * Expected DOM:
 *   <variant-picker data-option-index="1">
 *     <fieldset class="variant-picker__option" role="radiogroup" aria-labelledby="opt-label-1">
 *       <legend id="opt-label-1">Couleur</legend>
 *       <label class="variant-swatch" data-value="Rouge" data-color="#c0392b">
 *         <input type="radio" name="option1" value="Rouge" />
 *         <span class="variant-swatch__dot" aria-hidden="true"></span>
 *         <span class="variant-swatch__label">Rouge</span>
 *       </label>
 *       ...
 *     </fieldset>
 *   </variant-picker>
 *
 * Listens to parent ProductForm for variant-change and updates selection.
 */

import { Component } from '@theme/component';

export class VariantPicker extends Component {
  onConnect() {
    const radios = this.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      this.on(radio, 'change', (e) => this.#handleChange(e));
    });

    // Keyboard nav: arrow keys cycle through radios in the group
    this.on(this, 'keydown', (e) => this.#handleKeydown(e));
  }

  #handleChange() {
    const options = this.#collectOptions();
    this.emit('variant-picker:change', { options });

    // Bubble up to parent product-form (legacy Alpine compat)
    const form = this.closest('product-form');
    if (form && typeof form.findVariant === 'function') {
      const variant = form.findVariant(options);
      if (variant) form.setVariant(variant);
    }
  }

  #collectOptions() {
    const inputs = this.querySelectorAll('input[type="radio"]:checked');
    const options = [null, null, null];
    inputs.forEach((input) => {
      // Prefer data-option-position (explicit positional index 1/2/3) for
      // themes using Shopify's native `name="options[Color]"` form format.
      // Fallback to regex on name="option1" for generic theme compat.
      const posAttr = input.dataset.optionPosition || input.closest('[data-option-position]')?.dataset.optionPosition;
      if (posAttr) {
        const idx = parseInt(posAttr, 10) - 1;
        if (idx >= 0 && idx < 3) options[idx] = input.value;
        return;
      }
      const match = input.name.match(/option(\d)/);
      if (match) {
        const idx = parseInt(match[1], 10) - 1;
        options[idx] = input.value;
      }
    });
    return options;
  }

  #handleKeydown(e) {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    const fieldset = e.target.closest('fieldset');
    if (!fieldset) return;
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const currentIdx = radios.indexOf(e.target);
    if (currentIdx < 0) return;

    e.preventDefault();
    let nextIdx;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (currentIdx + 1) % radios.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx = (currentIdx - 1 + radios.length) % radios.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = radios.length - 1;
        break;
    }
    const next = radios[nextIdx];
    next.checked = true;
    next.focus();
    next.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
